"use client";

import { useEffect, useState } from "react";
import { useLocale, useTranslations } from "next-intl";
import type { Walkthrough } from "@engine/walkthrough";
import type { CellIdx } from "@engine/types";

interface Props {
  premiseKey: string;
  puzzleId: string;
  placements: ReadonlyArray<number | null>;
  fetchWalkthrough: (input: unknown) => Promise<Walkthrough | null>;
  /** Light up the squares a step is about while it is hovered or focused. */
  onHighlightCells: (cells: CellIdx[]) => void;
}

/**
 * How the case was solved, once you have solved it.
 *
 * Fetched rather than shipped with the page: it gives the whole answer away, so the server only
 * hands it over to someone who can already show a correct board.
 */
export function WalkthroughPanel({
  premiseKey, puzzleId, placements, fetchWalkthrough, onHighlightCells }: Props) {
  const t = useTranslations("walkthrough");
  const premise = useTranslations(`premises.${premiseKey}`);
  const locale = useLocale();
  const [walkthrough, setWalkthrough] = useState<Walkthrough | null>(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || walkthrough || loading) return;
    setLoading(true);
    void fetchWalkthrough({ puzzleId, placements, locale })
      .then((result) => setWalkthrough(result))
      .finally(() => setLoading(false));
  }, [open, walkthrough, loading, fetchWalkthrough, puzzleId, placements, locale]);

  return (
    <section
      data-testid="walkthrough"
      style={{ border: "1px solid rgba(128,128,128,0.35)", borderRadius: "0.5rem", padding: "1rem" }}
    >
      <button
        type="button"
        data-testid="walkthrough-toggle"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          font: "inherit",
          fontWeight: 700,
          fontSize: "1.05rem",
          color: "inherit",
          cursor: "pointer",
        }}
      >
        {open ? "▾ " : "▸ "}
        {t("title")}
      </button>

      {open && (
        <div style={{ marginTop: "0.75rem" }}>
          {loading && <p style={{ color: "var(--muted)", margin: 0 }}>{t("loading")}</p>}

          {!loading && !walkthrough && (
            <p data-testid="walkthrough-unavailable" style={{ color: "var(--muted)", margin: 0 }}>
              {t("unavailable")}
            </p>
          )}

          {walkthrough && (
            <>
              <ol data-testid="walkthrough-steps" style={{ display: "grid", gap: "0.35rem", paddingLeft: "1.3rem" }}>
                {walkthrough.steps.map((step) => (
                  <li
                    key={step.index}
                    onPointerEnter={() => onHighlightCells(step.cells)}
                    onPointerLeave={() => onHighlightCells([])}
                    style={{ cursor: "default" }}
                  >
                    {t(stepKey(step.key), step.params)}
                  </li>
                ))}
              </ol>

              {walkthrough.verdict && (
                <p data-testid="walkthrough-verdict" style={{ marginBottom: 0, fontWeight: 600 }}>
                  {premise("verdict", {
                    murderer: walkthrough.verdict.murderer,
                    victim: walkthrough.verdict.victim,
                  })}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}

/** The engine namespaces its keys; the message catalogue does not. */
const stepKey = (key: string): string => key.replace(/^walkthrough\./, "");
